import { createApi } from "@reduxjs/toolkit/query/react";
import { baseAuthQuery } from "../base/baseApi";
import { ApiResponse } from "../base/apiResponse";
import {
  FilterDto,
  ProtocolGradeRequest,
  ProtocolGradeResponse,
  ProtocolRequest,
  ProtocolResponse,
  ProtocolStudentFilter,
  ProtocolStudentResponse,
  ProtocolVersionResponse,
  ResultMessageResponse,
  ResultMessageResponseOf,
  RecogntionProtocolRequest,
  RecognitionProtocolResponse,
  ProtocolSubjectResponse,
} from "../../models/dtos";

export const protocolsApi = createApi({
  reducerPath: "protocolsApi",
  baseQuery: baseAuthQuery("protocols"),
  tagTypes: ["Protocol", "ProtocolGrades"],
  endpoints: (builder) => ({
    getProtocols: builder.query<ApiResponse<{ data: ProtocolResponse[]; totalRecords: number }>, Partial<FilterDto>>({
      query: (query) => ({
        url: "paginated",
        method: "POST",
        body: query,
      }),
      providesTags: (result) =>
        result && result.data && Array.isArray(result.data.data)
          ? [
              ...result.data.data.map(({ protocolId }) => ({ type: "Protocol" as const, id: protocolId })),
              { type: "Protocol", id: "LIST" },
            ]
          : [{ type: "Protocol", id: "LIST" }],
    }),

    getTeacherProtocols: builder.query<ApiResponse<ProtocolResponse[]>, number>({
      query: (teacherId) => ({
        url: `GetTeacherProtocols/${teacherId}`,
        method: "GET",
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ protocolId }) => ({ type: "Protocol" as const, id: protocolId })),
              { type: "Protocol", id: "LIST" },
            ]
          : [{ type: "Protocol", id: "LIST" }],
    }),

    getProtocol: builder.query<ApiResponse<ProtocolResponse>, number>({
      query: (protocolId) => ({
        url: `admin/${protocolId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, protocolId) => [{ type: "Protocol", id: protocolId }],
    }),

    getRecognitionProtocol: builder.query<ApiResponse<RecognitionProtocolResponse>, number>({
      query: (protocolId) => ({
        url: `admin/recognition/${protocolId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, protocolId) => [{ type: "Protocol", id: protocolId }],
    }),

    getProtocolGrades: builder.query<ApiResponse<ProtocolGradeResponse[]>, number>({
      query: (protocolId) => ({
        url: `GetProtocolGrades/${protocolId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, protocolId) => [{ type: "ProtocolGrades", id: protocolId }],
    }),

    getProtocolVersions: builder.query<ApiResponse<ProtocolVersionResponse[]>, number>({
      query: (protocolId) => ({
        url: `GetProtocolVersions/${protocolId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, protocolId) => [{ type: "Protocol", id: protocolId }],
    }),

    updateProtocol: builder.mutation<ApiResponse<number | null>, Partial<ProtocolRequest | ProtocolResponse>>({
      query: (protocol) => ({
        url: "CreateOrUpdate",
        method: "POST",
        body: protocol,
      }),
    }),

    updateRecognitionProtocol: builder.mutation<
      ApiResponse<number | null>,
      Partial<RecogntionProtocolRequest | ProtocolResponse>
    >({
      query: (protocol) => ({
        url: "CreateOrUpdateRecognitionProtocol",
        method: "POST",
        body: protocol,
      }),
    }),

    addProtocolStudentByFn: builder.mutation<ApiResponse<boolean>, { protocolId: number; fn: string }>({
      query: ({ protocolId, fn }) => ({
        url: `AddProtocolStudentByFn/${protocolId}/${fn}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { protocolId }) => [
        { type: "Protocol", id: protocolId },
        { type: "Protocol", id: "LIST" },
        { type: "ProtocolGrades", id: protocolId },
      ],
    }),

    removeProtocolStudent: builder.mutation<ApiResponse<boolean>, { protocolId: number; studentId: number }>({
      query: ({ protocolId, studentId }) => ({
        url: `RemoveProtocolStudent/${protocolId}/${studentId}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { protocolId }) => [
        { type: "Protocol", id: protocolId },
        { type: "Protocol", id: "LIST" },
        { type: "ProtocolGrades", id: protocolId },
      ],
    }),

    addProtocolSubjectById: builder.mutation<
      ApiResponse<boolean>,
      { protocolId: number; subjectId: number; semester: number }
    >({
      query: ({ protocolId, subjectId, semester }) => ({
        url: `AddProtocolSubjectById/${protocolId}/${subjectId}/${semester}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { protocolId }) => [
        { type: "Protocol", id: protocolId },
        { type: "Protocol", id: "LIST" },
        { type: "ProtocolGrades", id: protocolId },
      ],
    }),

    removeProtocolSubject: builder.mutation<
      ApiResponse<boolean>,
      { protocolId: number; semester: number; subjectId: number }
    >({
      query: ({ protocolId, semester, subjectId }) => ({
        url: `RemoveProtocolSubject/${protocolId}/${semester}/${subjectId}/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { protocolId }) => [
        { type: "Protocol", id: protocolId },
        { type: "Protocol", id: "LIST" },
        { type: "ProtocolGrades", id: protocolId },
      ],
    }),

    deleteProtocol: builder.mutation<ApiResponse<boolean>, number>({
      query: (protocolId) => ({
        url: `Delete/${protocolId}`,
        method: "POST",
      }),
      invalidatesTags: (result, _error, protocolId) =>
        result?.data === true
          ? [
              { type: "Protocol", id: "LIST" },
              { type: "Protocol", id: protocolId },
            ]
          : [],
    }),

    downloadWorkingProtocol: builder.query<
      { blob: Blob; fileName: string },
      { protocolId: number; format: "pdf" | "excel" }
    >({
      query: ({ protocolId, format }) => ({
        url: `DownloadWorkingProtocol/${protocolId}`,
        method: "GET",
        params: { format },
        responseHandler: async (response) => {
          const blob = await response.blob();
          const contentDisposition = response.headers.get("Content-Disposition") || "";
          let fileName = "";

          const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]*)/i);
          if (utf8Match && utf8Match[1]) {
            fileName = decodeURIComponent(utf8Match[1]);
          } else {
            const match = contentDisposition.match(/filename="([^"]*)"/i);
            if (match && match[1]) {
              fileName = match[1];
            }
          }

          return { blob, fileName };
        },
      }),
    }),

    downloadSignedProtocol: builder.query<{ blob: Blob; fileName: string }, { protocolId: number }>({
      query: ({ protocolId }) => ({
        url: `DownloadSignedProtocol/${protocolId}`,
        method: "GET",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const contentDisposition = response.headers.get("Content-Disposition") || "";
          let fileName = "";

          const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]*)/i);
          if (utf8Match && utf8Match[1]) {
            fileName = decodeURIComponent(utf8Match[1]);
          } else {
            const match = contentDisposition.match(/filename="([^"]*)"/i);
            if (match && match[1]) {
              fileName = match[1];
            }
          }

          return { blob, fileName };
        },
      }),
    }),

    checkProtocolSignatureByProtocolId: builder.mutation<ApiResponse<boolean>, number>({
      query: (protocolId) => ({
        url: `CheckProtocolSignature/${protocolId}`,
        method: "GET",
      }),
      invalidatesTags: (_result, _error, protocolId) => [{ type: "Protocol", id: protocolId }],
    }),

    generateProtocolStudentList: builder.query<
      ApiResponse<ProtocolStudentResponse[]>,
      { protocolId: number; filter: ProtocolStudentFilter }
    >({
      query: ({ protocolId, filter }) => ({
        url: `GenerateProtocolStudentList/${protocolId}`,
        method: "GET",
        params: filter,
      }),
      providesTags: (_result, _error, { protocolId }) => [{ type: "Protocol", id: protocolId }],
    }),

    generateProtocolSubjectList: builder.query<ApiResponse<ProtocolSubjectResponse[]>, { protocolId: number }>({
      query: ({ protocolId }) => ({
        url: `generateRecognitionProtocolSubjectList/${protocolId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { protocolId }) => [{ type: "Protocol", id: protocolId }],
    }),

    setProtocolStudents: builder.mutation<
      ApiResponse<boolean>,
      { protocolId: number; protocolStudents: ProtocolStudentResponse[] }
    >({
      query: ({ protocolId, protocolStudents }) => ({
        url: `SetProtocolStudents/${protocolId}`,
        method: "POST",
        body: protocolStudents,
      }),
      invalidatesTags: (_result, _error, { protocolId }) => [
        { type: "Protocol", id: protocolId },
        { type: "Protocol", id: "LIST" },
        { type: "ProtocolGrades", id: protocolId },
      ],
    }),

    setProtocolSubjects: builder.mutation<
      ApiResponse<boolean>,
      { protocolId: number; protocolSubjects: ProtocolSubjectResponse[] }
    >({
      query: ({ protocolId, protocolSubjects }) => ({
        url: `SetProtocolSubjects/${protocolId}`,
        method: "POST",
        body: protocolSubjects,
      }),
      invalidatesTags: (_result, _error, { protocolId }) => [
        { type: "Protocol", id: protocolId },
        { type: "Protocol", id: "LIST" },
        { type: "ProtocolGrades", id: protocolId },
      ],
    }),

    adminUpdateStudentGrade: builder.mutation<ApiResponse<boolean>, ProtocolGradeRequest>({
      query: (protocolGrade) => ({
        url: `admin/UpdateStudentGrade`,
        method: "POST",
        body: protocolGrade,
      }),
    }),

    adminUpdateSubjectGrade: builder.mutation<ApiResponse<boolean>, ProtocolGradeRequest>({
      query: (protocolGrade) => ({
        url: `admin/UpdateStudentGrade`,
        method: "POST",
        body: protocolGrade,
      }),
    }),

    createProtocolVersion: builder.mutation<ApiResponse<boolean>, number>({
      query: (protocolId) => ({
        url: `CreateProtocolVersion/${protocolId}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, protocolId) => [
        { type: "Protocol", id: protocolId },
        { type: "Protocol", id: "LIST" },
      ],
    }),

    generateProtocolsFromPlan: builder.mutation<
      ApiResponse<ResultMessageResponse>,
      { planId: number; sessionTypeId: number; examTypeId: number; academicYearId: number }
    >({
      query: (body) => ({
        url: "GenerateProtocolsFromPlan",
        method: "POST",
        body,
      }),
      invalidatesTags: () => [{ type: "Protocol", id: "LIST" }],
    }),

    copyProtocol: builder.mutation<ApiResponse<ResultMessageResponseOf<number | null>>, number>({
      query: (protocolId) => ({
        url: `CopyProtocol/${protocolId}`,
        method: "POST",
      }),
      // invalidatesTags: () => [{ type: "Protocol", id: "LIST" }],
    }),

    checkDuplicateProtocol: builder.mutation<ApiResponse<ProtocolResponse | null>, Partial<ProtocolRequest>>({
      query: (protocol) => ({
        url: "CheckDuplicate",
        method: "POST",
        body: protocol,
      }),
    }),
  }),
});
